import { faceMark } from '../components/face-mark.js';
import { h, toast, type View } from '../components/dom.js';
import type { SessionController, AuthState } from '../../session/controller.js';
import type { ApiError } from '../../api/client.js';
import { ASIS_MOBILE_SERVER_BASE, getMobileBridge } from '../../platform/mobile-bridge.js';

function authBrand(): HTMLElement {
  const brand = h('div', { class: 'auth-brand' });
  brand.innerHTML = `${faceMark()}<span>ASIS</span>`;
  return brand;
}

export function createLoginView(controller: SessionController, state: AuthState): View {
  const root = h('section', { class: 'view view-auth', id: 'view-login' });
  const card = h('div', { class: 'auth-card' });

  let active = true;
  let mode: 'login' | 'register' = 'login';
  let registrationState: { enabled: boolean; known: boolean } = { enabled: false, known: false };

  if (state.kind === 'anonymous' && (state.reason === 'secure_session_unavailable' || state.reason === 'logout_pending')) {
    const closing = state.reason === 'logout_pending';
    const retry = h('button', { type: 'button', class: 'btn primary block' }, [closing ? 'Reintentar cierre de sesión' : 'Reintentar recuperación']);
    const error = h('p', { class: 'auth-error', role: 'alert' });
    card.append(authBrand(), h('h1', { class: 'auth-title' }, [closing ? 'Completar cierre de sesión' : 'Recuperar tu sesión']),
      h('p', { class: 'auth-sub' }, [closing ? 'Esta app ya no utiliza tu sesión. Falta confirmar el cierre con el servidor o borrar su copia segura. Reintenta cuando tengas conexión.' : 'ASIS no ha podido abrir el almacenamiento seguro de este dispositivo. Tu sesión guardada no se ha borrado.']), retry, error);
    retry.addEventListener('click', async () => {
      retry.disabled = true; error.textContent = '';
      try { await controller.retry(); }
      catch { if (active) error.textContent = closing ? 'No se ha podido completar el cierre. Vuelve a intentarlo.' : 'No se ha podido recuperar la sesión. Vuelve a intentarlo.'; }
      finally { if (active) retry.disabled = false; }
    });
    root.append(card);
    return { el: root, destroy() { active = false; } };
  }

  const titleEl = h('h1', { class: 'auth-title' }, ['Hola. Soy ASIS.']);
  const subEl = h('p', { class: 'auth-sub' }, ['Entra en tu cuenta para continuar.']);
  const modeBadge = h('p', { class: 'auth-mode-badge', role: 'note' });
  card.append(authBrand(), titleEl, subEl, modeBadge);

  const form = h('form', { class: 'form-stack', novalidate: 'true' });

  const emailLabel = h('label', { class: 'field' }, []);
  emailLabel.append(h('span', {}, ['Correo electrónico']));
  const emailInput = h('input', { class: 'input', type: 'email', autocomplete: 'email', required: 'true', spellcheck: 'false', autocapitalize: 'none', maxlength: '254' });
  emailLabel.appendChild(emailInput);

  const displayNameLabel = h('label', { class: 'field hidden', 'data-register-only': 'true' }, []);
  displayNameLabel.append(h('span', {}, ['Nombre visible']));
  const displayNameInput = h('input', { class: 'input', type: 'text', autocomplete: 'name', maxlength: '64' });
  displayNameLabel.appendChild(displayNameInput);

  const passLabel = h('label', { class: 'field' }, []);
  passLabel.append(h('span', {}, ['Contraseña']));
  const passInput = h('input', { class: 'input', type: 'password', autocomplete: 'current-password', required: 'true', minlength: '12' });
  passLabel.appendChild(passInput);

  const confirmLabel = h('label', { class: 'field hidden', 'data-register-only': 'true' }, []);
  confirmLabel.append(h('span', {}, ['Repite la contraseña']));
  const confirmInput = h('input', { class: 'input', type: 'password', autocomplete: 'new-password', minlength: '12' });
  confirmLabel.appendChild(confirmInput);

  const submit = h('button', { type: 'submit', class: 'btn primary block' }, ['Entrar']);
  const submitHint = h('p', { class: 'auth-hint' }, ['Mínimo 12 caracteres. Usa algo memorable para ti.']);

  const switchRow = h('div', { class: 'auth-switch' });
  const switchBtn = h('button', { type: 'button', class: 'btn ghost block', 'aria-label': 'Cambiar entre iniciar sesión y crear cuenta' }, ['Crear cuenta nueva']);
  switchRow.appendChild(switchBtn);
  const switchHelp = h('p', { class: 'auth-switch-help' }, ['Si el registro está abierto en este servidor, también puedes crear tu cuenta.']);

  const error = h('p', { class: 'auth-error', role: 'alert' });
  if (state.kind === 'anonymous' && state.reason === 'expired') error.textContent = 'La sesión caducó. Vuelve a iniciar sesión.';

  form.append(emailLabel, displayNameLabel, passLabel, confirmLabel, submit, submitHint, switchRow, switchHelp, error);
  card.appendChild(form);

  if (state.kind === 'anonymous' && state.reason === 'offline') error.textContent = 'No podemos conectar con el servidor. Comprueba la dirección o vuelve a intentarlo.';

  card.appendChild(serverControls(controller));
  root.appendChild(card);

  function paintMode(): void {
    if (!active) return;
    switchBtn.disabled = mode === 'login' && !registrationState.enabled;
    switchRow.hidden = mode === 'login' && !registrationState.enabled;
    switchHelp.hidden = true;
    submitHint.hidden = mode === 'login';
    modeBadge.hidden = mode === 'login';
    if (mode === 'register') {
      titleEl.textContent = 'Crear cuenta';
      subEl.textContent = 'La misma cuenta en tu móvil, tu Mac y tu ASIS.';
      submit.textContent = 'Crear cuenta y entrar';
      submitHint.textContent = 'La contraseña debe tener al menos 12 caracteres.';
      displayNameLabel.classList.remove('hidden');
      confirmLabel.classList.remove('hidden');
      emailInput.setAttribute('autocomplete', 'email');
      passInput.setAttribute('autocomplete', 'new-password');
      switchBtn.textContent = 'Ya tengo cuenta, iniciar sesión';
      switchHelp.textContent = 'El servidor decidirá si permite nuevas cuentas ahora mismo.';
      modeBadge.textContent = registrationState.known ? (registrationState.enabled ? 'Registro abierto' : 'Registro cerrado') : 'Comprobando registro…';
      modeBadge.dataset['tone'] = registrationState.known ? (registrationState.enabled ? 'positive' : 'muted') : 'muted';
    } else {
      titleEl.textContent = 'Hola. Soy ASIS.';
      subEl.textContent = 'Entra en tu cuenta para continuar.';
      submit.textContent = 'Entrar';
      submitHint.textContent = 'Mínimo 12 caracteres. Usa algo memorable para ti.';
      displayNameLabel.classList.add('hidden');
      confirmLabel.classList.add('hidden');
      emailInput.setAttribute('autocomplete', 'email');
      passInput.setAttribute('autocomplete', 'current-password');
      switchBtn.textContent = registrationState.enabled ? 'Crear cuenta nueva' : 'Crear cuenta nueva (si el servidor lo permite)';
      switchHelp.textContent = 'Si el registro está abierto en este servidor, también puedes crear tu cuenta.';
      modeBadge.textContent = registrationState.known ? (registrationState.enabled ? 'Registro abierto' : 'Registro cerrado en este servidor') : '';
      modeBadge.dataset['tone'] = registrationState.known ? (registrationState.enabled ? 'positive' : 'muted') : 'muted';
    }
  }

  switchBtn.addEventListener('click', () => {
    if (!active || submit.disabled || (mode === 'login' && !registrationState.enabled)) return;
    mode = mode === 'login' ? 'register' : 'login';
    passInput.value = ''; confirmInput.value = '';
    error.textContent = '';
    paintMode();
  });

  paintMode();

  const registrationAbort = new AbortController();
  const api = controller.getApi();
  void (async (): Promise<void> => {
    try {
      const status = await api.getStatus(registrationAbort.signal);
      if (!active || registrationAbort.signal.aborted) return;
      registrationState = { enabled: status.registrationEnabled === true, known: true };
      paintMode();
    } catch (err) {
      if (!active || registrationAbort.signal.aborted) return;
      registrationState = { enabled: false, known: true };
      paintMode();
    }
  })();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!active || submit.disabled) return;
    const email = emailInput.value.trim();
    const password = passInput.value;
    const displayName = displayNameInput.value.trim();
    const confirm = confirmInput.value;
    if (mode === 'register') {
      if (!registrationState.enabled) { error.textContent = 'El registro no está disponible.'; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) { error.textContent = 'Escribe un correo válido.'; return; }
      if (!displayName) { error.textContent = 'Indica un nombre visible.'; return; }
      if (displayName.length > 64) { error.textContent = 'Nombre visible demasiado largo (máx. 64).'; return; }
      if (password.length < 12) { error.textContent = 'La contraseña debe tener al menos 12 caracteres.'; return; }
      if (password !== confirm) { error.textContent = 'Las contraseñas no coinciden.'; return; }
    } else {
      if (!email) { error.textContent = 'Indica tu correo.'; return; }
      if (!password) { error.textContent = 'Indica tu contraseña.'; return; }
    }
    switchBtn.disabled = true;
    submit.disabled = true; error.textContent = '';
    try {
      if (mode === 'register') {
        await controller.register({ email, displayName, password });
      } else {
        await controller.login(email, password);
      }
    } catch (err) {
      if (!active) return;
      error.textContent = (err as ApiError).message ?? (err as Error).message ?? 'No se pudo completar la acción.';
    } finally {
      if (active) { submit.disabled = false; paintMode(); }
    }
  });

  return { el: root, destroy() { active = false; registrationAbort.abort(); passInput.value = ''; confirmInput.value = ''; } };
}

export function createSetupView(controller: SessionController): View {
  const root = h('section', { class: 'view view-auth', id: 'view-setup' });
  const card = h('div', { class: 'auth-card' });
  card.append(
    authBrand(),
    h('h1', { class: 'auth-title' }, ['Bienvenido a ASIS']),
    h('p', { class: 'auth-sub' }, ['Crea tu primera cuenta para empezar.']),
  );
  const form = h('form', { class: 'form-stack' });
  const tokLabel = h('label', { class: 'field' }, []);
  tokLabel.append(h('span', {}, ['Clave de primer arranque']));
  const tokInput = h('input', { class: 'input', type: 'password', autocomplete: 'off', required: 'true' });
  tokLabel.appendChild(tokInput);
  const emailLabel = h('label', { class: 'field' }, []);
  emailLabel.append(h('span', {}, ['Correo electrónico']));
  const emailInput = h('input', { class: 'input', type: 'email', autocomplete: 'email', required: 'true', spellcheck: 'false', autocapitalize: 'none', maxlength: '254' });
  emailLabel.appendChild(emailInput);
  const dnLabel = h('label', { class: 'field' }, []);
  dnLabel.append(h('span', {}, ['Nombre visible']));
  const dnInput = h('input', { class: 'input', type: 'text', required: 'true' });
  dnLabel.appendChild(dnInput);
  const passLabel = h('label', { class: 'field' }, []);
  passLabel.append(h('span', {}, ['Contraseña (mín. 12)']));
  const passInput = h('input', { class: 'input', type: 'password', autocomplete: 'new-password', minlength: '12', required: 'true' });
  passLabel.appendChild(passInput);
  const submit = h('button', { type: 'submit', class: 'btn primary block' }, ['Crear cuenta']);
  const error = h('p', { class: 'auth-error', role: 'alert' });
  form.append(tokLabel, emailLabel, dnLabel, passLabel, submit, error);
  card.appendChild(form);
  card.appendChild(serverControls(controller));
  root.appendChild(card);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (submit.disabled) return;
    submit.disabled = true;
    error.textContent = '';
    try {
      await controller.setup({
        setupToken: tokInput.value.trim(),
        email: emailInput.value.trim(),
        displayName: dnInput.value.trim(),
        password: passInput.value,
      });
    } catch (err) {
      error.textContent = (err as Error).message;
      toast((err as Error).message, 'error');
    } finally {
      submit.disabled = false;
    }
  });

  return { el: root, destroy() {} };
}

function serverControls(controller: SessionController): HTMLElement {
  const details = h('details', { class: 'server-details' });
  details.append(h('summary', {}, ['Conexión al servidor']));
  if (getMobileBridge()) {
    const retry = h('button', {type:'button',class:'btn ghost'}, ['Reintentar conexión']);
    const error = h('p', {class:'auth-error',role:'alert'});
    details.append(h('p', {class:'card-sub'}, [ASIS_MOBILE_SERVER_BASE]),
      h('p', {class:'card-sub'}, ['La aplicación móvil conecta con tu cuenta de ASIS.']), retry, error);
    retry.addEventListener('click', async () => {
      retry.disabled=true; error.textContent='';
      try { await controller.retry(); }
      catch { if (details.isConnected) error.textContent='No se pudo recuperar la conexión. Vuelve a intentarlo.'; }
      finally { retry.disabled=false; }
    });
    return details;
  }
  const form = h('form', { class: 'form-stack' });
  const input = h('input', { type: 'url', class: 'input', required: true }); input.value = controller.getServerBase();
  const save = h('button', { type: 'submit', class: 'btn ghost' }, ['Cambiar servidor']);
  const retry = h('button', { type: 'button', class: 'btn ghost' }, ['Reintentar conexión']);
  const error = h('p', { role: 'alert', class: 'auth-error' });
  form.append(h('label', { class: 'field' }, [h('span', {}, ['Dirección del servidor']), input]), save, retry, error);
  form.addEventListener('submit', async e => { e.preventDefault(); if (save.disabled) return; save.disabled = true; try { await controller.switchServerBase(input.value.trim()); } catch (err) { error.textContent = (err as Error).message; } finally { save.disabled = false; } });
  retry.addEventListener('click', async () => { retry.disabled = true; try { await controller.retry(); } finally { retry.disabled = false; } });
  details.append(form); return details;
}
